import { useState } from "react";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { refreshRecord, type RefreshResource } from "@/lib/api";
import { cn } from "@/lib/utils";

interface RefreshButtonProps {
  resource: RefreshResource;
  id: string;
  label: string;
  onRefreshed?: () => void;
  className?: string;
}

export function RefreshButton({
  resource,
  id,
  label,
  onRefreshed,
  className,
}: RefreshButtonProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setPending(true);
    setError(null);
    try {
      await refreshRecord(resource, id);
      onRefreshed?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Refresh failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={pending}
      title={error ?? `Refresh ${label}`}
      aria-label={`Refresh ${label}`}
      className={cn(error && "text-destructive", className)}
    >
      <RefreshCw
        className={cn("h-3.5 w-3.5", pending && "animate-spin")}
        aria-hidden
      />
    </Button>
  );
}
